import { useState } from 'react'
import { createRootRoute, Link, Outlet } from '@tanstack/react-router'
import { useAuthState } from '@/hooks/useAuthState'
import { Button, LoginForm } from '@smorgrav/velpr-ui'
import logoUrl from '@smorgrav/velpr-ui/assets/logo.svg'
import { auth } from '@/firebase'
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut
} from 'firebase/auth'

function RootLayout() {
  const { user, isRoot, loading } = useAuthState()
  const [loginError, setLoginError] = useState<string | null>(null)

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/40 p-4">
        <LoginForm
          className="w-full max-w-sm drop-shadow-lg"
          error={loginError}
          onEmailLogin={async (email: string, password: string) => {
            setLoginError(null)
            try {
              await signInWithEmailAndPassword(auth, email, password)
            } catch (err) {
              setLoginError(err instanceof Error ? err.message : 'Login failed')
            }
          }}
          onGoogleLogin={async () => {
            setLoginError(null)
            try {
              await signInWithPopup(auth, new GoogleAuthProvider())
            } catch (err) {
              setLoginError(err instanceof Error ? err.message : 'Login failed')
            }
          }}
        />
      </div>
    )
  }

  // Signed in but not a platform root — the admin app is root-only, so don't
  // render the shell or any nav at all; offer only a way back out.
  if (!isRoot) {
    return (
      <div className="bg-muted/40 flex h-full items-center justify-center p-4">
        <div className="bg-background flex max-w-sm flex-col items-center gap-4 rounded-lg border p-8 text-center drop-shadow-lg">
          <img src={logoUrl} alt="Velpr" className="h-12 w-12" />
          <div>
            <h1 className="text-lg font-semibold">Ingen tilgang</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Kontoen <span className="font-medium">{user.email}</span> har ikke
              tilgang til admin.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => signOut(auth)}>
            Logg ut
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background text-foreground flex h-full flex-col">
      <header className="bg-primary text-primary-foreground flex items-center justify-between border-b px-6 py-3">
        <nav className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoUrl} alt="Velpr" className="h-9 w-9" />
            <span className="text-lg font-semibold">Velpr Admin</span>
          </Link>
          <Link
            to="/"
            className="opacity-80 hover:opacity-100 [&.active]:font-medium [&.active]:opacity-100"
          >
            Dashboard
          </Link>
          <Link
            to="/customers"
            className="opacity-80 hover:opacity-100 [&.active]:font-medium [&.active]:opacity-100"
          >
            Kunder
          </Link>
          <Link
            to="/email"
            className="opacity-80 hover:opacity-100 [&.active]:font-medium [&.active]:opacity-100"
          >
            E-post
          </Link>
          <Link
            to="/formr"
            className="opacity-80 hover:opacity-100 [&.active]:font-medium [&.active]:opacity-100"
          >
            Formr
          </Link>
          <Link
            to="/auth"
            className="opacity-80 hover:opacity-100 [&.active]:font-medium [&.active]:opacity-100"
          >
            Auth
          </Link>
          <Link
            to="/users"
            className="opacity-80 hover:opacity-100 [&.active]:font-medium [&.active]:opacity-100"
          >
            Brukere
          </Link>
          <Link
            to="/maintenance"
            className="opacity-80 hover:opacity-100 [&.active]:font-medium [&.active]:opacity-100"
          >
            Vedlikehold
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <span className="text-sm opacity-80">{user.email}</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => signOut(auth)}
          >
            Sign out
          </Button>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-6">
        <Outlet />
      </main>
    </div>
  )
}

export const Route = createRootRoute({
  component: RootLayout
})
